import { apiClient, getErrorMessage } from './client'
import type { AuthMethodStatsResponse } from '@paperair/shared-types'

export { getErrorMessage }
export type { AuthMethodStatsResponse }

export type LoginAnomalyFlagName =
  | 'multi_ip'
  | 'multi_network'
  | 'multi_country'
  | 'impossible_travel'
  | 'datacenter_origin'
  | 'high_abuse_score'
  | 'unexpected_country'

export type FlagSeverity = 'informational' | 'warning'

export interface TriggeredFlag {
  flag: LoginAnomalyFlagName
  severity: FlagSeverity
  detail: string
}

export interface GeoData {
  continent?: string
  country?: string
  region?: string
  city?: string
  lat?: number
  lon?: number
  accuracyKm?: number
  asn?: number
  asOrg?: string
  isDatacenter?: boolean
}

export interface LoginActivityEvent {
  timestamp: string
  ip: string
  geo?: GeoData
  appId: string
  action: 'login' | 'session_ip_change'
  method?: string
  reputation?: {
    score: number
    usageType?: string
    isTor?: boolean
  }
}

export interface UserLoginActivityResponse {
  userId: string
  windowDays: number
  summary: {
    distinctIpCount: number
    distinctAsnCount: number
    distinctCountryCount: number
    apps: string[]
  }
  flags: TriggeredFlag[]
  severityScore: number
  events: LoginActivityEvent[]
}

export async function getUserLoginActivity(
  userId: string,
  days?: number
): Promise<UserLoginActivityResponse> {
  const response = await apiClient.get<UserLoginActivityResponse>(
    `/admin/users/${userId}/login-activity`,
    { params: days ? { days } : undefined }
  )
  return response.data
}

export interface FlaggedUser {
  userId: string
  email?: string
  flags: TriggeredFlag[]
  severityScore: number
  apps: string[]
  distinctIpCount: number
  distinctAsnCount: number
  distinctCountryCount: number
  mostRecentEventAt: string
}

export interface LoginFlagsResponse {
  windowDays?: number
  users: FlaggedUser[]
}

export async function getLoginFlags(days?: number): Promise<LoginFlagsResponse> {
  const response = await apiClient.get<LoginFlagsResponse>('/admin/security/login-flags', {
    params: days ? { days } : undefined,
  })
  return response.data
}

export interface DismissLoginFlagData {
  flag: LoginAnomalyFlagName
  days?: number
  reason?: string
}

export async function dismissLoginFlag(
  userId: string,
  data: DismissLoginFlagData
): Promise<{ message: string; userId: string; flag: string; until: string }> {
  const response = await apiClient.post(`/admin/security/login-flags/${userId}/dismiss`, data)
  return response.data
}

export async function getAuthMethodStats(days?: number): Promise<AuthMethodStatsResponse> {
  const response = await apiClient.get<AuthMethodStatsResponse>('/admin/stats/auth-methods', {
    params: days ? { days } : undefined,
  })
  return response.data
}
